import { Task, taskZod } from 'packages/lib/api/annotations/contract/task';
import { apiGetWithEtag } from 'packages/lib/api/annotations/rest';
import { z } from 'zod';
import { patch } from 'packages/lib/api/annotations/rest';
import { AnnotationState } from 'packages/lib/api/annotations/contract/enums';

/**
 * This Task Management service method is used specifically for use-cases where TTO, admin,
 * and management users modify Task.annotationState values for individual tasks.
 *
 * Data management UI's, like task lookup and sample-group management are typical use-cases.
 *
 * This method isn't concerned with task assignment, but it *must* enforce well-defined state
 * transitions. Therefore, when setting to an *InProgress state, an assignee must be provided.
 * When setting to a *ToDo, *Done, or other non-in-progress state, the assignee will be removed.
 *
 * There is a multi-task version of this method: updateManyTasksState
 */
export const updateTaskState = async (
  task: Task,
  nextAnnotationState: AnnotationState,
  assignee: string | null,
  comments?: string
): Promise<Task> => {
  const apiPath = `/tasks/${task.token}`;
  const { taskLatest, etag, payload } = await changeTaskState(
    task,
    nextAnnotationState,
    assignee,
    comments
  );
  if (taskLatest.annotationState !== task.annotationState) {
    throw new Error(
      `This task has been edited/modified. Expected annotation state ${task.annotationState} but the latest value is ${taskLatest.annotationState}.`
    );
  }
  await patch(apiPath, payload, { 'If-Match': etag });
  /**
   * TODO: Integrate user task-state-changed metrics
   */
  return { ...taskLatest, ...payload };
};

/**
 * Private internal function for the updateTaskState method.
 */
async function changeTaskState(
  task: Pick<Task, 'annotationState' | 'token'>,
  nextAnnotationState: AnnotationState,
  assignee: string | null,
  comments?: string
): Promise<{
  taskLatest: Task;
  etag: string;
  payload: Pick<Task, 'assignee' | 'annotationState' | 'comments'>;
}> {
  const { data: taskLatest, etag } = await apiGetWithEtag(
    `/tasks/${task.token}`,
    taskZod.parse
  );
  if (!etag) {
    throw new Error(
      `Response for /tasks/${task.token} returned invalid or undefined ETag headers`
    );
  }
  // TODO: Validate whether nextAnnotationState requires an assignee email or null value and throw/fulfill accordingly
  // TODO: Update annotationStateMachine to implement predicated state changes
  // const nextAnnotationState = annotationStateMachine(taskLatest).ASSIGN;
  const requiresAssignee =
    nextAnnotationState.toLocaleLowerCase().includes('inprogress') ||
    nextAnnotationState.toLocaleLowerCase().includes('inreview');
  if (requiresAssignee) {
    const { success: isValidEmail } = z.string().email().safeParse(assignee);
    if (!assignee || !isValidEmail) {
      throw new Error(
        `Invalid or missing email address: ${assignee}. Cannot set Task wannotationState to "${nextAnnotationState}" without an assignee.`
      );
    }
  }

  const requiresUnassignment = !requiresAssignee;
  if (!!assignee && requiresUnassignment) {
    throw new Error(
      `Cannot set Task annotationState to "${nextAnnotationState}" while task is assigned. Given next-assignee value is ${assignee}.`
    );
  }

  return {
    taskLatest,
    etag,
    payload: {
      assignee,
      annotationState: nextAnnotationState,
      comments: comments ?? taskLatest.comments,
    },
  };
}
